import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { documentUrl, documentDocxUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, DownloadSimple, FileImage, EnvelopeSimple, Newspaper, Signpost, Certificate, Files, Ruler, Lock, FileDoc } from "@phosphor-icons/react";

const DOCS = [
  // INICIALES
  { key: "contrato", labelKey: "doc_contrato", group: "core", icon: "contract" },
  { key: "solicitud_autorizacion", labelKey: "doc_solicitud", group: "core", icon: "contract" },
  { key: "solicitud_prorroga", labelKey: "doc_solicitud_prorroga", group: "core", icon: "contract" },

  // AVISOS / NOTIFICACIONES
  { key: "aviso_mensuras", labelKey: "doc_aviso_mensuras", group: "avisos", icon: "mail" },
  { key: "notificacion_colindante_norte", labelKey: "doc_notif_norte", group: "avisos", icon: "mail" },
  { key: "notificacion_colindante_sur", labelKey: "doc_notif_sur", group: "avisos", icon: "mail" },
  { key: "notificacion_colindante_este", labelKey: "doc_notif_este", group: "avisos", icon: "mail" },
  { key: "notificacion_colindante_oeste", labelKey: "doc_notif_oeste", group: "avisos", icon: "mail" },
  { key: "aviso_medio_ambiente", labelKey: "doc_aviso_medio_ambiente", group: "avisos", icon: "mail" },
  { key: "aviso_ayuntamiento", labelKey: "doc_aviso_ayuntamiento", group: "avisos", icon: "mail" },
  { key: "aviso_tribunal_tierras", labelKey: "doc_aviso_tribunal", group: "avisos", icon: "mail" },

  // PUBLICACIÓN / LETRERO
  { key: "publicacion_periodico", labelKey: "doc_publicacion", group: "publicacion", icon: "news" },
  { key: "letrero", labelKey: "doc_letrero", group: "publicacion", icon: "sign" },

  // MEDICIÓN
  { key: "acta_hitos", labelKey: "doc_acta_hitos", group: "medicion", icon: "ruler" },
  { key: "informe_tecnico", labelKey: "doc_informe_tecnico", group: "medicion", icon: "cert" },
  { key: "carta_conformidad", labelKey: "doc_carta_conformidad", group: "medicion", icon: "cert" },
  { key: "declaracion_posesion", labelKey: "doc_declaracion_posesion", group: "medicion", icon: "cert" },
  { key: "resultados_medicion", labelKey: "doc_resultados", group: "medicion", icon: "ruler" },

  // FOTOS
  { key: "fotos_linderos", labelKey: "doc_fotos_linderos", group: "fotos", isPhoto: true, icon: "img" },
  { key: "fotos_letrero", labelKey: "doc_fotos_letrero", group: "fotos", isPhoto: true, icon: "img" },

  // DUPLICADO POR PÉRDIDA
  { key: "instancia_duplicado", labelKey: "doc_instancia_duplicado", group: "duplicado", icon: "contract" },
  { key: "declaracion_jurada_perdida", labelKey: "doc_declaracion_jurada", group: "duplicado", icon: "cert" },
  { key: "compulsa_perdida", labelKey: "doc_compulsa", group: "duplicado", icon: "cert" },
  { key: "publicacion_perdida", labelKey: "doc_publicacion_perdida", group: "duplicado", icon: "news" },

  // POSESIÓN / MEJORA
  { key: "declaracion_posesion_mejora", labelKey: "doc_declaracion_posesion_mejora", group: "posesion", icon: "cert" },
  { key: "compulsa_posesion", labelKey: "doc_compulsa_posesion", group: "posesion", icon: "cert" },

  // DOCUMENTO INDEPENDIENTE (cualquier trámite)
  { key: "poder_representacion", labelKey: "doc_poder_representacion", group: "poder", icon: "contract" },
];

const GROUPS = {
  core: { en: "Initial documents", es: "Documentos iniciales" },
  avisos: { en: "Notifications", es: "Avisos y Notificaciones" },
  publicacion: { en: "Publication & sign", es: "Publicación y Letrero" },
  medicion: { en: "Survey & file", es: "Medición y Expediente Final" },
  fotos: { en: "Photo reports", es: "Reportes Fotográficos" },
  duplicado: { en: "Duplicate by loss", es: "Duplicado por Pérdida" },
  posesion: { en: "Possession & improvement", es: "Posesión / Mejora" },
  poder: { en: "Power of attorney", es: "Poder de Representación" },
};

function DocIcon({ icon }) {
  const props = { weight: "duotone", size: 18, color: "#ea580c" };
  switch (icon) {
    case "contract": return <FileText {...props} />;
    case "mail": return <EnvelopeSimple {...props} />;
    case "news": return <Newspaper {...props} />;
    case "sign": return <Signpost {...props} />;
    case "cert": return <Certificate {...props} />;
    case "ruler": return <Ruler {...props} />;
    case "img": return <FileImage {...props} color="#2563eb" />;
    default: return <Files {...props} />;
  }
}

export default function DocumentosTab({ exp }) {
  const { t, lang } = useLang();
  const { canViewDoc } = useAuth();
  const download = (docKey) => window.open(documentUrl(exp.id, docKey), "_blank");
  const downloadDocx = (docKey) => window.open(documentDocxUrl(exp.id, docKey), "_blank");
  const groups = exp.tipo_tramite === "duplicado_perdida"
    ? ["duplicado", "poder"]
    : exp.tipo_tramite === "posesion_mejora"
    ? ["posesion", "poder"]
    : ["core", "avisos", "publicacion", "medicion", "fotos", "poder"];

  return (
    <div className="space-y-6" data-testid="documentos-tab">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t.docs_section_title}</h2>
        <p className="text-slate-600 mt-1">{t.docs_section_desc}</p>
      </div>

      {groups.map((g) => {
        const items = DOCS.filter(d => d.group === g);
        const visible = items.filter(d => canViewDoc(d.key));
        if (visible.length === 0) return null;
        return (
          <div key={g} className="bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="coord-label">{GROUPS[g][lang]}</div>
              <div className="font-mono text-xs text-slate-400">{visible.length} docs</div>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((doc) => {
                const allowed = canViewDoc(doc.key);
                if (!allowed) return null;
                return (
                  <div
                    key={doc.key}
                    className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-sm border ${doc.isPhoto ? "bg-blue-50 border-blue-200" : "bg-orange-50 border-orange-200"}`}>
                        <DocIcon icon={doc.icon} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">{t[doc.labelKey]}</div>
                        <div className="font-mono text-xs text-slate-500">{doc.key}.pdf</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        data-testid={`gen-${doc.key}-btn`}
                        onClick={() => download(doc.key)}
                        size="sm"
                        variant="outline"
                        className="rounded-sm hover:bg-slate-900 hover:text-white hover:border-slate-900"
                      >
                        <DownloadSimple weight="bold" size={14} className="mr-1.5" />
                        PDF
                      </Button>
                      {!doc.isPhoto && (
                        <Button
                          data-testid={`gen-${doc.key}-docx-btn`}
                          onClick={() => downloadDocx(doc.key)}
                          size="sm"
                          variant="outline"
                          className="rounded-sm hover:bg-blue-700 hover:text-white hover:border-blue-700"
                        >
                          <FileDoc weight="bold" size={14} className="mr-1.5" />
                          Word
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
